/**
 * Fleet portal access constants shared by the console (invite drawer, client
 * record) and the server (invite route, portal access service) — CUR-1049.
 */

import { type FleetPortalRole, portalRolesOf } from '../../permissions/roles';
import type { FleetClientType, ProductTypeCategory } from '../../prisma/enums';
import { ORG_ROLES, type Role } from './user';

/**
 * Fleet capability — the portal, escalations and delegated-authority
 * decisions — belongs to the FLEET product category only. MOTOR keeps the
 * vehicle features (assessment, inspection) but a motor claim for an
 * individual has no fleet client behind it (decision, 4 Sep 2026).
 */
export const FLEET_PRODUCT_CATEGORY = 'FLEET' satisfies ProductTypeCategory;

export function isFleetProductCategory(category: ProductTypeCategory | null | undefined): boolean {
    return category === FLEET_PRODUCT_CATEGORY;
}

/**
 * Which fleet client record each portal role attaches to (CUR-1049, CUR-1099).
 * A fleet manager is a member of end clients and decides their authorisations;
 * a coordinator is a member of a consolidator and sees every client under it,
 * read-only. The invite route, the invitation service, the access service,
 * the invite drawer and the Portal access card all read this one map, so the
 * role-to-record pairing has a single home.
 */
export const PORTAL_ATTACHMENT = {
    [ORG_ROLES.FLEET_PORTAL_USER]: 'CLIENT',
    [ORG_ROLES.FMC_COORDINATOR]: 'CONSOLIDATOR',
} as const satisfies Record<FleetPortalRole, FleetClientType>;

export type PortalAttachment = (typeof PORTAL_ATTACHMENT)[FleetPortalRole];

/** The portal role a membership on a record of this type belongs to. */
export const PORTAL_ROLE_FOR_ATTACHMENT = {
    CLIENT: ORG_ROLES.FLEET_PORTAL_USER,
    CONSOLIDATOR: ORG_ROLES.FMC_COORDINATOR,
} as const satisfies Record<PortalAttachment, FleetPortalRole>;

/** How the portal role for each record type reads inside a sentence (acronym case kept). */
export const PORTAL_ROLE_NOUN = {
    CLIENT: 'fleet portal user',
    CONSOLIDATOR: 'FMC coordinator',
} as const satisfies Record<PortalAttachment, string>;

/** Human noun for the record a portal role attaches to, for copy and audit lines. */
export const PORTAL_ATTACHMENT_NOUN = {
    CLIENT: 'client',
    CONSOLIDATOR: 'consolidator',
} as const satisfies Record<PortalAttachment, string>;

/**
 * Who the portal is serving (CUR-1100). The two personas share every read and
 * differ only in chrome: a fleet manager decides authorisations and works one
 * client; an FMC coordinator oversees a portfolio, read-only, with a client
 * scope selector and a Client column. Resolved on the server from the roles
 * and returned by `fleetPortal.getPortalContext`, so the client never infers
 * it from client counts or permissions.
 */
export const PORTAL_PERSONAS = ['FLEET_MANAGER', 'FMC_COORDINATOR'] as const;
export type PortalPersona = (typeof PORTAL_PERSONAS)[number];

export const PORTAL_PERSONA_FOR_ROLE = {
    [ORG_ROLES.FLEET_PORTAL_USER]: 'FLEET_MANAGER',
    [ORG_ROLES.FMC_COORDINATOR]: 'FMC_COORDINATOR',
} as const satisfies Record<FleetPortalRole, PortalPersona>;

/**
 * The persona a user's roles put them in; null without a portal role. A user
 * holds one portal role (`assertOnePortalRole`); should legacy data carry both,
 * the fleet manager persona wins, matching the decision path which only ever
 * reads the fleet manager's rows.
 */
export function portalPersonaOf(roles: readonly Role[]): PortalPersona | null {
    const role = portalRolesOf(roles)[0];
    return role === undefined ? null : PORTAL_PERSONA_FOR_ROLE[role];
}

/** Lifecycle of a portal user as shown on the client record's Portal access card. */
export const PORTAL_USER_STATUSES = ['INVITED', 'ACTIVE', 'REVOKED'] as const;
export type PortalUserStatus = (typeof PORTAL_USER_STATUSES)[number];

/** Largest per-user or per-client authority the UI accepts (ex GST, matches Decimal(12,2)). */
export const MAX_FLEET_AUTHORITY_LIMIT = 9_999_999_999.99;

/**
 * Reserved for a future revision-safe Clear21 approval relay (CUR-1052).
 * No runtime code reads this flag today: the relay is unconditionally disabled.
 * Do not enable dispatch until Clear21 atomically enforces the approved quote
 * fingerprint and amount; API permissions and a flag alone are insufficient.
 */
export const ENABLE_CLEAR21_APPROVAL_WRITEBACK_FLAG = 'enable-clear21-approval-writeback';

/**
 * CUR-1107: daily reconciliation of repairer records against the Clear21 repairer list (fills
 * `Supplier.clear21RepairerId`, flags drift, raises data-quality tasks) plus the vendor-management
 * summary, column, filter and "check now" action. Off by default; remove after rollout.
 */
export const ENABLE_CLEAR21_IDENTITY_RECONCILIATION_FLAG = 'enable-clear21-identity-reconciliation';

/**
 * CUR-1658: sends the org's repairer records to its Clear21 company and keeps them up to date
 * (create, change, archive on removal), plus the vendor-management sync status, filter and "Send to
 * Clear21" action. A Clear21 company's repairer list is shared by every work provider in it, so turn
 * this on for one org per Clear21 company only. Off by default; remove after rollout.
 */
export const ENABLE_CLEAR21_REPAIRER_SYNC_FLAG = 'enable-clear21-repairer-sync';

/**
 * CUR-1106: automated dispatch to Clear21 targets a Curium repairer record by its Clear21 external
 * code only, never by name; a record without a code (or one Clear21 does not list) falls back to a
 * manual booking and a task. Off by default; remove after rollout.
 */
export const ENABLE_CLEAR21_DISPATCH_GATE_FLAG = 'enable-clear21-dispatch-gate';

/** Why a repairer could not be dispatched to automatically (CUR-1106). */
export const CLEAR21_MANUAL_BOOKING_REASONS = [
    'no_code',
    'not_in_clear21',
    'duplicate_code',
    'not_integrated',
] as const;

export type Clear21ManualBookingReason = (typeof CLEAR21_MANUAL_BOOKING_REASONS)[number];

export const CLEAR21_MANUAL_BOOKING_REASON_LABELS = {
    no_code: 'the repairer record has no Clear21 external code',
    not_in_clear21: 'no repairer in Clear21 carries its external code',
    duplicate_code: 'more than one repairer in Clear21 carries its external code',
    not_integrated: 'the repairer is not integrated on iBodyshop in Clear21',
} as const satisfies Record<Clear21ManualBookingReason, string>;

/**
 * The platform default for an organisation's delegated authority (ex GST),
 * seeded on the `fleet_default_delegated_authority` setting. A client without
 * its own authority limit is measured against the organisation's value, which
 * starts here (decision, JLo, 7 Sep 2026: seed at $5,000).
 */
export const DEFAULT_FLEET_DELEGATED_AUTHORITY = 5000;

/** Longest decline reason or query the portal accepts. */
export const AUTHORISATION_NOTE_MAX_LENGTH = 2000;

/**
 * Sections of the portal claim page a tenant can switch off without a deploy
 * (CUR-1069: visibility rules are configuration). The header — claim
 * reference, status, vehicle — is never optional.
 */
export const PORTAL_CLAIM_VIEW_SECTIONS = [
    'claimStates',
    'claimDetails',
    'repairProgression',
    'repairAssessment',
    'damageScan',
] as const;
export type PortalClaimViewSection = (typeof PORTAL_CLAIM_VIEW_SECTIONS)[number];

export type FleetPortalClaimViewConfiguration = {
    sections: Record<PortalClaimViewSection, boolean>;
};

/** Shipped default: the whole strip list. */
export const DEFAULT_FLEET_PORTAL_CLAIM_VIEW: FleetPortalClaimViewConfiguration = {
    sections: {
        claimStates: true,
        claimDetails: true,
        repairProgression: true,
        repairAssessment: true,
        damageScan: true,
    },
};
